import {timezoneSafeTimestamp} from '../core/time.js';

export const PlannerSourceKind=Object.freeze({
  RECIPE:'recipe',
  SESSION:'session'
});

export const PlannerBlockKind=Object.freeze({
  PREPARATION:'preparation',
  CURING:'curing',
  DRYING:'drying',
  STAGE:'stage',
  BUFFER:'buffer'
});

export const PlannerDurationKind=Object.freeze({
  FIXED:'fixed',
  ESTIMATE:'estimate',
  MISSING:'missing'
});

const SOURCE_KINDS=new Set(Object.values(PlannerSourceKind));
const BLOCK_KINDS=new Set(Object.values(PlannerBlockKind));
const DURATION_KINDS=new Set(Object.values(PlannerDurationKind));
const MAX_DURATION_MINUTES=365*24*60;
const MAX_BUFFER_MINUTES=24*60;

function required(value,label,max=180){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalText(value,label,max=240){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function enumValue(value,allowed,label){
  const key=String(value??'').trim().toLowerCase();
  if(!allowed.has(key))throw new TypeError('Unsupported '+label);
  return key;
}

function order(value,label='sequence'){
  const number=Number(value??0);
  if(!Number.isInteger(number)||number<0||number>1000000){
    throw new TypeError(label+' must be a non-negative integer');
  }
  return number;
}

function duration(value,kind){
  if(kind===PlannerDurationKind.MISSING){
    if(value!=null&&value!=='')throw new TypeError('Missing duration cannot carry durationMinutes');
    return null;
  }
  const number=Number(value);
  if(!Number.isInteger(number)||number<1||number>MAX_DURATION_MINUTES){
    throw new TypeError('durationMinutes must be a positive whole number of minutes');
  }
  return number;
}

function buffer(value){
  const number=Number(value??0);
  if(!Number.isInteger(number)||number<0||number>MAX_BUFFER_MINUTES){
    throw new TypeError('bufferMinutes must be an integer between 0 and '+MAX_BUFFER_MINUTES);
  }
  return number;
}

function optionalTimeZone(value){
  if(value==null||value==='')return null;
  const zone=String(value).trim();
  if(zone.length>120)throw new TypeError('timeZone is too long');
  try{
    new Intl.DateTimeFormat('en',{timeZone:zone}).format(new Date(0));
  }catch{
    throw new TypeError('Invalid IANA timezone');
  }
  return zone;
}

function sourceReference(value,{requiredRef=true}={}){
  if(value==null){
    if(requiredRef)throw new TypeError('sourceRef is required');
    return null;
  }
  if(typeof value!=='object'||Array.isArray(value))throw new TypeError('sourceRef must be an object');
  return Object.freeze({
    kind:required(value.kind,'sourceRef.kind',80),
    id:required(value.id,'sourceRef.id',180),
    field:required(value.field,'sourceRef.field',120)
  });
}

export function createPlannerSourceBlock({
  id,
  kind,
  label,
  durationMinutes=null,
  durationKind=durationMinutes==null?PlannerDurationKind.MISSING:PlannerDurationKind.FIXED,
  sourceRef,
  sequence=0,
  itemId=null,
  stageType=null
}={}){
  const blockKind=enumValue(kind,BLOCK_KINDS,'planner block kind');
  if(blockKind===PlannerBlockKind.BUFFER)throw new TypeError('Buffer is a schedule assumption, not a source block');
  const confidence=enumValue(durationKind,DURATION_KINDS,'planner duration kind');
  return Object.freeze({
    id:required(id,'id',180),
    kind:blockKind,
    label:required(label,'label',180),
    durationMinutes:duration(durationMinutes,confidence),
    durationKind:confidence,
    sourceRef:sourceReference(sourceRef),
    sequence:order(sequence),
    itemId:optionalText(itemId,'itemId',180),
    stageType:optionalText(stageType,'stageType',80)
  });
}

export function createPlannerItemChain({
  itemId,
  itemName,
  blocks=[]
}={}){
  if(!Array.isArray(blocks))throw new TypeError('blocks must be an array');
  const normalized=blocks.map(createPlannerSourceBlock)
    .sort((a,b)=>a.sequence-b.sequence||a.id.localeCompare(b.id));
  if(normalized.some(block=>block.kind===PlannerBlockKind.STAGE)){
    throw new TypeError('Item preparation chain cannot contain stage blocks');
  }
  const ids=new Set();
  for(const block of normalized){
    if(ids.has(block.id))throw new TypeError('Duplicate planner block id');
    ids.add(block.id);
  }
  return Object.freeze({
    itemId:required(itemId,'itemId',180),
    itemName:required(itemName,'itemName',180),
    blocks:Object.freeze(normalized)
  });
}

export function createPlannerSource({
  kind,
  id,
  itemChains=[],
  processBlocks=[]
}={}){
  const sourceKind=enumValue(kind,SOURCE_KINDS,'planner source kind');
  if(!Array.isArray(itemChains))throw new TypeError('itemChains must be an array');
  if(!Array.isArray(processBlocks))throw new TypeError('processBlocks must be an array');

  const chains=itemChains.map(createPlannerItemChain)
    .sort((a,b)=>a.itemName.localeCompare(b.itemName,'pl')||a.itemId.localeCompare(b.itemId));
  const process=processBlocks.map(createPlannerSourceBlock)
    .sort((a,b)=>a.sequence-b.sequence||a.id.localeCompare(b.id));

  if(process.some(block=>block.kind!==PlannerBlockKind.STAGE)){
    throw new TypeError('Process blocks must be stage blocks');
  }

  const ids=new Set();
  for(const block of [...process,...chains.flatMap(chain=>chain.blocks)]){
    if(ids.has(block.id))throw new TypeError('Planner source block ids must be unique');
    ids.add(block.id);
  }

  return Object.freeze({
    kind:sourceKind,
    id:required(id,'source id',180),
    itemChains:Object.freeze(chains),
    processBlocks:Object.freeze(process)
  });
}

export function createPlannerRequest({
  source,
  targetReadyAt,
  timeZone=null,
  bufferMinutes=0
}={}){
  return Object.freeze({
    source:createPlannerSource(source),
    targetReadyAt:timezoneSafeTimestamp(targetReadyAt,'targetReadyAt'),
    timeZone:optionalTimeZone(timeZone),
    bufferMinutes:buffer(bufferMinutes)
  });
}

export function createPlannerScheduledBlock({
  id,
  kind,
  label,
  durationMinutes,
  durationKind,
  startAt=null,
  endAt=null,
  sourceRef=null,
  sequence=0,
  lane='process',
  itemId=null,
  stageType=null
}={}){
  const blockKind=enumValue(kind,BLOCK_KINDS,'planner block kind');
  const confidence=enumValue(durationKind,DURATION_KINDS,'planner duration kind');
  const minutes=duration(durationMinutes,confidence);
  const start=startAt==null?null:timezoneSafeTimestamp(startAt,'startAt');
  const end=endAt==null?null:timezoneSafeTimestamp(endAt,'endAt');
  if((start==null)!=(end==null)){
    if(confidence!==PlannerDurationKind.MISSING){
      throw new TypeError('Scheduled block start/end must both be present or absent');
    }
  }
  if(start&&end){
    const elapsed=Math.round((Date.parse(end)-Date.parse(start))/60000);
    if(elapsed<0)throw new TypeError('Planner block end cannot be before start');
    if(minutes!=null&&elapsed!==minutes)throw new TypeError('Planner block timestamps must match durationMinutes');
  }
  return Object.freeze({
    id:required(id,'id',180),
    kind:blockKind,
    label:required(label,'label',180),
    durationMinutes:minutes,
    durationKind:confidence,
    startAt:start,
    endAt:end,
    sourceRef:sourceReference(sourceRef,{requiredRef:blockKind!==PlannerBlockKind.BUFFER}),
    sequence:order(sequence),
    lane:required(lane,'lane',80),
    itemId:optionalText(itemId,'itemId',180),
    stageType:optionalText(stageType,'stageType',80)
  });
}

export function createPlannerWarning({
  code,
  message,
  blockId=null,
  sourceRef=null
}={}){
  return Object.freeze({
    code:required(code,'warning code',80),
    message:required(message,'warning message',400),
    blockId:optionalText(blockId,'blockId',180),
    sourceRef:sourceRef==null?null:sourceReference(sourceRef)
  });
}

export function createPlannerSchedule({
  request,
  blocks=[],
  warnings=[],
  complete=false,
  processStartAt=null,
  processEndAt=null,
  earliestStartAt=null
}={}){
  const input=createPlannerRequest(request);
  if(!Array.isArray(blocks))throw new TypeError('blocks must be an array');
  if(!Array.isArray(warnings))throw new TypeError('warnings must be an array');
  const normalizedBlocks=blocks.map(createPlannerScheduledBlock);
  const normalizedWarnings=warnings.map(createPlannerWarning);
  const processStart=processStartAt==null?null:timezoneSafeTimestamp(processStartAt,'processStartAt');
  const processEnd=processEndAt==null?null:timezoneSafeTimestamp(processEndAt,'processEndAt');
  const earliest=earliestStartAt==null?null:timezoneSafeTimestamp(earliestStartAt,'earliestStartAt');
  if(processStart&&processEnd&&Date.parse(processEnd)<Date.parse(processStart)){
    throw new TypeError('processEndAt cannot be before processStartAt');
  }
  return Object.freeze({
    request:input,
    blocks:Object.freeze(normalizedBlocks),
    warnings:Object.freeze(normalizedWarnings),
    complete:Boolean(complete),
    processStartAt:processStart,
    processEndAt:processEnd,
    earliestStartAt:earliest,
    assumptions:Object.freeze({
      targetReadyAt:input.targetReadyAt,
      timeZone:input.timeZone,
      bufferMinutes:input.bufferMinutes,
      explicitDurationsOnly:true,
      itemPreparationRunsInParallel:true
    })
  });
}

export function plannerSessionTimes(schedule){
  const value=createPlannerSchedule(schedule);
  if(!value.complete||!value.processStartAt||!value.processEndAt){
    throw new Error('PLANNER_SCHEDULE_INCOMPLETE');
  }
  return Object.freeze({
    plannedStartAt:value.processStartAt,
    plannedEndAt:value.processEndAt
  });
}
